import { useCallback, useEffect, useRef, useState, type RefObject } from "react";

interface UseAutoScrollOptions {
  /** Distance from bottom (px) to consider "at bottom". Default: 80 */
  threshold?: number;
  /** Smooth scroll for new messages vs instant for programmatic jumps. Default: true */
  smooth?: boolean;
}

interface UseAutoScrollReturn {
  /** Whether the user has scrolled away from the bottom */
  isScrolledUp: boolean;
  /** Call when scroll event fires on the container */
  handleScroll: () => void;
  /** Jump to bottom (called by scroll-to-bottom FAB) */
  scrollToBottom: () => void;
  /** Reset scroll state (call after sending a message) */
  resetScroll: () => void;
}

/**
 * Manages auto-scroll behavior for a chat container.
 *
 * - Tracks whether the user has scrolled up from the bottom
 * - Auto-scrolls to bottom when new content arrives (if not scrolled up)
 * - Provides scrollToBottom() for the FAB
 * - Uses smooth scrolling for natural feel
 */
export function useAutoScroll(
  scrollRef: RefObject<HTMLDivElement | null>,
  deps: unknown[],
  options: UseAutoScrollOptions = {},
): UseAutoScrollReturn {
  const { threshold = 80, smooth = true } = options;
  const [isScrolledUp, setIsScrolledUp] = useState(false);
  const userScrolledRef = useRef(false);

  const handleScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;

    const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < threshold;
    userScrolledRef.current = !nearBottom;
    setIsScrolledUp(!nearBottom);
  }, [scrollRef, threshold]);

  const scrollToBottom = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;

    userScrolledRef.current = false;
    setIsScrolledUp(false);

    el.scrollTo({
      top: el.scrollHeight,
      behavior: smooth ? "smooth" : "instant",
    });
  }, [scrollRef, smooth]);

  const resetScroll = useCallback(() => {
    userScrolledRef.current = false;
    setIsScrolledUp(false);
  }, []);

  // Auto-scroll when deps change (new messages, streaming content)
  useEffect(() => {
    if (!userScrolledRef.current && scrollRef.current) {
      const el = scrollRef.current;
      // Use smooth for small increments (streaming), instant for big jumps (initial load)
      const distanceToBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
      const behavior = smooth && distanceToBottom < 500 ? "smooth" : "instant";
      el.scrollTo({ top: el.scrollHeight, behavior });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return { isScrolledUp, handleScroll, scrollToBottom, resetScroll };
}
